/**
 * One "Overall" cell in the profile hero (APP_SPEC §13): a vertical
 * `[icon] / LABEL / VALUE` stack on a small parchment plaque, centred on both
 * axes, with a wider gap under the icon than between the label and its value
 * so the value never visually collides with the label.
 *
 * The plaque is not decoration: the hero's theme artwork renders at full
 * strength behind these, so the totals need their own quiet ground to stay
 * legible over whatever background a player equips.
 *
 * This used to carry a second `size="game"` variant for the denser cells on a
 * game card, with a different value scale and no plaque. It had no call site —
 * game cards use `GameStat`, whose horizontal `[icon] [label/value]` form is a
 * deliberately different composition (see that file), not this one shrunk. The
 * dead variant is removed rather than kept "in case": it was also the only
 * thing that made this component's value scale look inconsistent with
 * `GameStat`'s, which it never actually rendered beside.
 *
 * The icon is decorative — the label always carries the meaning — so it is
 * `aria-hidden` (docs/UI_GUIDELINES.md §9).
 */
export interface ProfileStatTileProps {
  iconSrc: string;
  label: string;
  value: string;
}

export function ProfileStatTile({ iconSrc, label, value }: ProfileStatTileProps) {
  return (
    // `justify-center` as well as `items-center`: the plaque is a flex column
    // with equal top and bottom padding, so the icon/label/value stack is
    // centred on both axes of whatever height the row settles at, rather than
    // being packed against the top of a stretched cell.
    <div className="flex min-w-0 flex-col items-center justify-center rounded-[4px] border border-border bg-parchment/95 px-2 py-3.5 text-center shadow-[inset_0_1px_0_rgb(255_255_255/0.75),0_1px_2px_rgb(75_46_34/0.12)]">
      <img src={iconSrc} alt="" aria-hidden="true" className="h-9 w-9 shrink-0 object-contain" />
      {/* Dark ink, at full strength, like the value beneath it: the label is a
          printed word on parchment, not a washed-out caption. Gold and brown
          are reserved for the plate's icons, rules and ornament — a brown
          label reads as decoration rather than as data. It stays subordinate
          to its value through size and letterspacing instead. */}
      <span className="mt-2 font-profile text-[0.88rem] font-semibold uppercase leading-tight tracking-[0.055em] text-textPrimary">
        {label}
      </span>
      {/* Alegreya's default figures are *oldstyle* — 0 sits at x-height and 3
          and 4 descend — which is right for a date inside a sentence and wrong
          for a column of totals, where it reads as a lowercase o and as
          uneven baselines. Lining, tabular figures: every digit the same
          height and the same width, so the five plaques' numbers align as a
          row of readings. */}
      <span className="mt-1 font-profile text-[1.9rem] font-bold leading-[1.15] text-textPrimary [font-variant-numeric:lining-nums_tabular-nums] tablet:text-[2.05rem]">
        {value}
      </span>
    </div>
  );
}
